import React, { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, ModalBase, notify, Spinner } from '@bynder/design-system';
import { GenAIModal, isExtensionSupported, OriginalAsset } from '@bynder/image-gen-frontend-lib';
import { useTranslate } from '@bynder/localization';
import useAccessRights from 'packages/hooks/useAccessRights';
import { getBynderAccessToken, getPortalDomain } from 'packages/store/platform/platform.selectors';
import AssetsPicker from 'packages/pages/components/AssetsPickerModal/components/AssetsPicker';
import Sidebar from 'packages/pages/components/AssetsPickerModal/components/AssetsPickerSidebar';
import UploadButton from 'packages/pages/components/Uploader/UploadButton';
import useActions from 'packages/pages/components/AssetsPickerModal/components/useActions';
import { StyledModalBasedContent } from 'packages/pages/components/AssetsPickerModal/components/index.styled';
import { AssetGroup } from 'packages/pages/editor/ContentPickerModal/types';
import assetsService from 'packages/services/assetsService';
import features from '~/configs/features';
import { isAllowedImageFormat } from './utils';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: ({ enhancementId, fileId }: { enhancementId: number; fileId: string }) => Promise<void>;
    sessionId: string;
    originalAsset: OriginalAsset;
    saveButtonLabel?: string;
}

function ImageGen({ isOpen, onClose, onSave, sessionId, originalAsset, saveButtonLabel }: Props) {
    const { isPlatformAllowed } = useAccessRights();
    const { translate } = useTranslate();
    const token = useSelector(getBynderAccessToken);
    const portalDomain = useSelector(getPortalDomain);
    type ShowAssetPicker = NonNullable<ComponentProps<typeof GenAIModal>['showAssetPicker']>;
    type AddAssetsCallback = Parameters<ShowAssetPicker>[0];
    type AssetPickerPayload = Parameters<AddAssetsCallback>[0];

    const [addAssetsCallback, setAddAssetsCallback] = useState<AddAssetsCallback | undefined>(undefined);
    const [isLocalAssetLoading, setIsLocalAssetLoading] = useState(false);

    const {
        assetType,
        wrapperRef,
        collectionId,
        selectedLocalAsset,
        allowInternalAsset,
        handleDamClick,
        setSelectedCollection,
        onLocalAssetClick,
    } = useActions({});

    const isEnabled = isPlatformAllowed([features.EDIT_WITH_AI]);

    const getJWT = useCallback(async () => {
        const testToken = localStorage.getItem('testGenAIToken');

        if (testToken) {
            return testToken;
        }

        return token;
    }, [token]);

    const hostURL = useMemo(() => {
        const testHostURL = localStorage.getItem('testGenAIHostURL');

        if (testHostURL) {
            return testHostURL;
        }

        // with trim last slash if exists
        return `https://${portalDomain}`;
    }, [portalDomain]);

    const handleAssetPickerClose = useCallback(() => {
        setAddAssetsCallback(undefined);
    }, []);

    const showAssetPicker = useCallback((callback: AddAssetsCallback) => {
        setAddAssetsCallback(() => callback);
    }, []);

    const closeAssetPicker = useCallback(() => {
        setAddAssetsCallback(undefined);
    }, []);

    const handleAssetPickerSelect = useCallback(
        (
            [asset],
            metadata?: {
                selectedFile?: {
                    thumbnail?: string;
                    thumbnailUrl?: string;
                    previewUrl?: string;
                    url?: string;
                    thumbnails?: Record<string, string>;
                };
            },
        ) => {
            if (!asset || !addAssetsCallback) {
                handleAssetPickerClose();

                return;
            }

            const rawDatabaseId = asset?.databaseId ?? asset?.id;
            const databaseId = rawDatabaseId ? String(rawDatabaseId) : undefined;

            if (!databaseId) {
                handleAssetPickerClose();

                return;
            }

            const extension = asset?.extensions?.[0] || asset?.extension || '';

            if (!extension || !isExtensionSupported(extension.toLowerCase())) {
                notify({
                    title: translate('modal.assetsPicker.error.title.invalidFileType'),
                    variant: 'error',
                });

                return;
            }

            const selectedFile = metadata?.selectedFile;

            // Compact View surfaces thumbnails in different fields, prefer the most specific ones.
            const thumbnail =
                selectedFile?.thumbnail ||
                selectedFile?.thumbnailUrl ||
                selectedFile?.previewUrl ||
                selectedFile?.thumbnails?.['320x320'] ||
                selectedFile?.url ||
                asset?.thumbnails?.['320x320'] ||
                asset?.thumbnails?.['1920x1920'] ||
                asset?.thumbnail ||
                asset?.thumbnailUrl ||
                asset?.previewUrls?.[0] ||
                asset?.previewUrl ||
                asset?.originalUrl ||
                '';

            const payload: AssetPickerPayload = [
                {
                    databaseId,
                    thumbnail: thumbnail || undefined,
                },
            ];

            addAssetsCallback(payload);

            handleAssetPickerClose();
        },
        [addAssetsCallback, handleAssetPickerClose, translate],
    );

    const assetPickerCompactViewProps = useMemo(
        () => ({
            language: 'en_US',
            mode: 'SingleSelect',
            assetTypes: ['IMAGE'],
            isContainerMode: true,
            hideSwitch: true,
            selectedAssets: [],
            onSuccess: handleAssetPickerSelect,
        }),
        [handleAssetPickerSelect],
    );

    const handleLocalAssetConfirm = useCallback(async () => {
        if (!addAssetsCallback || !selectedLocalAsset) {
            return;
        }

        const isFormatCompatible = isAllowedImageFormat(selectedLocalAsset);

        if (!isFormatCompatible) {
            notify({
                title: translate('modal.assetsPicker.error.title.invalidFileType'),
                variant: 'error',
            });

            return;
        }

        setIsLocalAssetLoading(true);

        try {
            const { aiEditData } = await assetsService.prepareAIEdit(selectedLocalAsset.id);

            const thumbnail =
                selectedLocalAsset.thumbnails?.['320x320'] ||
                Object.values(selectedLocalAsset.thumbnails ?? {})[0] ||
                '';

            const payload: AssetPickerPayload = [
                {
                    fileId: aiEditData.fileId,
                    thumbnail: thumbnail || undefined,
                },
            ];

            addAssetsCallback(payload);
            handleAssetPickerClose();
        } catch {
            notify({
                title: translate('modal.assetsPicker.error.title.generic'),
                variant: 'error',
            });
        } finally {
            setIsLocalAssetLoading(false);
        }
    }, [addAssetsCallback, selectedLocalAsset, handleAssetPickerClose, translate]);

    const isAssetPickerOpen = Boolean(addAssetsCallback);

    if (!isEnabled) {
        return null;
    }

    return (
        <>
            <GenAIModal
                getJWT={getJWT}
                originalAsset={originalAsset}
                sessionId={sessionId}
                isOpen={isOpen}
                onClose={onClose}
                onSave={onSave}
                allowedTools={['REMOVE_BACKGROUND', 'REPLACE_BACKGROUND', 'RESIZE', 'SMART_EDIT']}
                saveButtonLabel={saveButtonLabel || translate('image-gen.modal.apply-changes')}
                isDownloadAvailable
                hostURL={hostURL}
                clientName="STUDIO"
                showAssetPicker={showAssetPicker}
                closeAssetPicker={closeAssetPicker}
            />

            <ModalBase
                key={`asset-picker-${originalAsset?.fileId}-${isOpen}`}
                size="fullscreen"
                isOpen={isAssetPickerOpen}
                onClose={handleAssetPickerClose}
            >
                {isAssetPickerOpen && (
                    <>
                        <ModalBase.Header
                            title={translate('modal.assetsPicker.button.add_asset')}
                            additionalActions={
                                assetType === AssetGroup.LOCAL ? (
                                    <UploadButton
                                        iconType="upload"
                                        assetTypes={['image']}
                                        collectionId={collectionId}
                                        multiple
                                    />
                                ) : undefined
                            }
                        />
                        {allowInternalAsset && (
                            <ModalBase.Sidebar>
                                <Sidebar
                                    assetType={assetType}
                                    type="image"
                                    selectedCollectionId={collectionId}
                                    setSelectedCollection={setSelectedCollection}
                                    handleDamClick={handleDamClick}
                                />
                            </ModalBase.Sidebar>
                        )}
                        <ModalBase.Content>
                            <StyledModalBasedContent isLocal={assetType === AssetGroup.LOCAL} ref={wrapperRef}>
                                <AssetsPicker
                                    type="image"
                                    collectionId={collectionId}
                                    assetType={assetType}
                                    selectedItem={selectedLocalAsset}
                                    onSelectLocal={onLocalAssetClick}
                                    compactViewProps={assetPickerCompactViewProps}
                                    isPersonalUploadsAllowed
                                    filterTypes={['IMAGE']}
                                />
                                {isLocalAssetLoading && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        }}
                                    >
                                        <Spinner />
                                    </div>
                                )}
                            </StyledModalBasedContent>
                        </ModalBase.Content>
                        {assetType === AssetGroup.LOCAL && selectedLocalAsset && (
                            <ModalBase.Footer
                                actionPrimary={null}
                                actionSecondary={
                                    <Button
                                        variant="primary"
                                        isLoading={isLocalAssetLoading}
                                        onClick={handleLocalAssetConfirm}
                                    >
                                        {translate('modal.assetsPicker.button.add_asset')}
                                    </Button>
                                }
                            />
                        )}
                    </>
                )}
            </ModalBase>
        </>
    );
}

export default React.memo(ImageGen);
